"use client";
import {
  __async
} from "../chunk-M66XENHI.js";
import { getBaseMPCNetworkUrl, WalletScheme, WalletType } from "@getpara/core-sdk";
import * as uuid from "uuid";
const configCGGMPBase = (serverUrl, walletId, id) => `{"ServerUrl":"${serverUrl}", "WalletId": "${walletId}", "Id":"${id}", "Ids":["USER","CAPSULE"], "Threshold":1}`;
const configDKLSBase = (walletId, id, disableWebSockets) => `{"walletId": "${walletId}", "id":"${id}", "otherId":"CAPSULE", "isReceiver": false, "disableWebSockets": ${disableWebSockets}}`;
function keygenRequest(ctx, userId, walletId, protocolId) {
  return __async(this, null, function* () {
    const { data } = yield ctx.mpcComputationClient.post("/wallets", {
      userId,
      walletId,
      protocolId
    });
    return data;
  });
}
function signMessageRequest(ctx, userId, walletId, protocolId, message, signer) {
  return __async(this, null, function* () {
    const { data } = yield ctx.mpcComputationClient.post(`/wallets/${walletId}/messages/sign`, {
      userId,
      protocolId,
      message,
      signer
    });
    return data;
  });
}
function sendTransactionRequest(ctx, userId, walletId, protocolId, transaction, signer, chainId) {
  return __async(this, null, function* () {
    const { data } = yield ctx.mpcComputationClient.post(`/wallets/${walletId}/transactions/send`, {
      userId,
      protocolId,
      transaction,
      signer,
      chainId
    });
    return data;
  });
}
function ed25519Keygen(ctx, userId) {
  return __async(this, null, function* () {
    const { walletId, protocolId } = yield ctx.client.createWallet(userId, {
      scheme: WalletScheme.ED25519,
      type: WalletType.SOLANA
    });
    const serverUrl = getBaseMPCNetworkUrl(ctx.env, !ctx.disableWebSockets);
    try {
      const newSigner = yield new Promise(
        (resolve, reject) => globalThis.ed25519CreateAccount(serverUrl, walletId, protocolId, (err, result) => {
          if (err) {
            reject(err);
          }
          resolve(result);
        })
      );
      return { signer: newSigner, walletId };
    } catch (e) {
      throw new Error(`error creating account of type SOLANA with userId ${userId} and walletId ${walletId}`);
    }
  });
}
function ed25519PreKeygen(ctx, pregenIdentifier, pregenIdentifierType) {
  return __async(this, null, function* () {
    const { walletId, protocolId } = yield ctx.client.createPregenWallet({
      pregenIdentifier,
      pregenIdentifierType,
      scheme: WalletScheme.ED25519,
      type: WalletType.SOLANA
    });
    const serverUrl = getBaseMPCNetworkUrl(ctx.env, !ctx.disableWebSockets);
    try {
      const newSigner = yield new Promise(
        (resolve, reject) => globalThis.ed25519CreateAccount(serverUrl, walletId, protocolId, (err, result) => {
          if (err) {
            reject(err);
          }
          resolve(result);
        })
      );
      return { signer: newSigner, walletId };
    } catch (e) {
      throw new Error(`error creating account of type SOLANA with walletId ${walletId}`);
    }
  });
}
function ed25519Sign(ctx, share, userId, walletId, base64Bytes) {
  return __async(this, null, function* () {
    const protocolId = uuid.v4();
    const preSignMessageRes = ctx.client.preSignMessage(
      userId,
      walletId,
      base64Bytes,
      WalletScheme.ED25519,
      void 0,
      protocolId
    );
    const signRes = function() {
      return __async(this, null, function* () {
        try {
          const base64Sig = yield new Promise(
            (resolve, reject) => globalThis.ed25519Sign(share, protocolId, base64Bytes, (err, result) => {
              if (err) {
                reject(err);
              }
              resolve(result);
            })
          );
          return { signature: base64Sig };
        } catch (e) {
          throw new Error(`error signing for account of type SOLANA with userId ${userId} and walletId ${walletId}`);
        }
      });
    }();
    yield preSignMessageRes;
    return yield signRes;
  });
}
function keygen(ctx, userId, type, secretKey) {
  return __async(this, null, function* () {
    const { walletId, protocolId } = yield ctx.client.createWallet(userId, {
      useTwoSigners: true,
      scheme: ctx.useDKLS ? WalletScheme.DKLS : WalletScheme.CGGMP,
      type,
      cosmosPrefix: type === WalletType.COSMOS ? ctx.cosmosPrefix : void 0
    });
    if (ctx.offloadMPCComputationURL && !ctx.useDKLS) {
      return {
        signer: (yield keygenRequest(ctx, userId, walletId, protocolId)).signer,
        walletId
      };
    }
    const serverUrl = getBaseMPCNetworkUrl(ctx.env, !ctx.disableWebSockets);
    const signerConfigUser = ctx.useDKLS ? configDKLSBase(walletId, "USER", ctx.disableWebSockets) : configCGGMPBase(serverUrl, walletId, "USER");
    const createAccountFn = ctx.useDKLS ? globalThis.dklsCreateAccount : globalThis.createAccountV2;
    try {
      const newSigner = yield new Promise(
        (resolve, reject) => createAccountFn(
          signerConfigUser,
          serverUrl,
          protocolId,
          secretKey,
          () => {
          },
          // no-op for deprecated callback to update progress percentage
          (err, result) => {
            if (err) {
              reject(err);
            }
            resolve(result);
          }
        )
      );
      return { signer: newSigner, walletId };
    } catch (e) {
      throw new Error(`error creating account of type ${type} with userId ${userId} and walletId ${walletId}`);
    }
  });
}
function preKeygen(ctx, _partnerId, pregenIdentifier, pregenIdentifierType, type, secretKey) {
  return __async(this, null, function* () {
    const { walletId, protocolId } = yield ctx.client.createPregenWallet({
      pregenIdentifier,
      pregenIdentifierType,
      type,
      cosmosPrefix: type === WalletType.COSMOS ? ctx.cosmosPrefix : void 0
    });
    const serverUrl = getBaseMPCNetworkUrl(ctx.env, !ctx.disableWebSockets);
    const signerConfigUser = configDKLSBase(walletId, "USER", ctx.disableWebSockets);
    try {
      const newSigner = yield new Promise(
        (resolve, reject) => globalThis.dklsCreateAccount(
          signerConfigUser,
          serverUrl,
          protocolId,
          secretKey,
          () => {
          },
          // no-op for deprecated callback to update progress percentage
          (err, result) => {
            if (err) {
              reject(err);
            }
            resolve(result);
          }
        )
      );
      return { signer: newSigner, walletId };
    } catch (e) {
      throw new Error(`error creating account of type ${type} with walletId ${walletId}`);
    }
  });
}
function signMessage(ctx, share, walletId, userId, message, cosmosSignDoc) {
  return __async(this, null, function* () {
    const protocolId = uuid.v4();
    const preSignMessageRes = ctx.client.preSignMessage(userId, walletId, message, null, cosmosSignDoc, protocolId);
    if (ctx.offloadMPCComputationURL && !ctx.useDKLS) {
      return signMessageRequest(ctx, userId, walletId, protocolId, message, share);
    }
    const serverUrl = getBaseMPCNetworkUrl(ctx.env, !ctx.disableWebSockets);
    const signMessageFn = ctx.useDKLS ? globalThis.dklsSignMessage : globalThis.signMessage;
    const parsedShare = JSON.parse(share);
    if (!parsedShare.disableWebSockets !== !ctx.disableWebSockets) {
      parsedShare.disableWebSockets = ctx.disableWebSockets;
    }
    share = JSON.stringify(parsedShare);
    const signMessageRes = function() {
      return __async(this, null, function* () {
        try {
          return yield new Promise(
            (resolve, reject) => signMessageFn(share, serverUrl, message, protocolId, (err, result) => {
              if (err) {
                reject(err);
              }
              resolve({ signature: result });
            })
          );
        } catch (e) {
          throw new Error(`error signing for account with userId ${userId} and walletId ${walletId}`);
        }
      });
    }();
    const { pendingTransactionId } = yield preSignMessageRes;
    if (pendingTransactionId) {
      return { pendingTransactionId };
    }
    return yield signMessageRes;
  });
}
function signTransaction(ctx, share, walletId, userId, tx, chainId) {
  return __async(this, null, function* () {
    const protocolId = uuid.v4();
    const signTransactionRes = ctx.client.signTransaction(userId, walletId, { transaction: tx, chainId, protocolId });
    if (ctx.offloadMPCComputationURL && !ctx.useDKLS) {
      return sendTransactionRequest(ctx, userId, walletId, protocolId, tx, share, chainId);
    }
    const serverUrl = getBaseMPCNetworkUrl(ctx.env, !ctx.disableWebSockets);
    const signTransactionFn = ctx.useDKLS ? globalThis.dklsSendTransaction : globalThis.sendTransaction;
    const parsedShare = JSON.parse(share);
    if (!parsedShare.disableWebSockets !== !ctx.disableWebSockets) {
      parsedShare.disableWebSockets = ctx.disableWebSockets;
    }
    share = JSON.stringify(parsedShare);
    const signTxRes = function() {
      return __async(this, null, function* () {
        try {
          return yield new Promise(
            (resolve, reject) => signTransactionFn(share, serverUrl, tx, chainId, protocolId, (err, result) => {
              if (err) {
                reject(err);
              }
              resolve({ signature: result });
            })
          );
        } catch (e) {
          throw new Error(`error signing transaction for account with userId ${userId} and walletId ${walletId}`);
        }
      });
    }();
    const {
      data: { pendingTransactionId }
    } = yield signTransactionRes;
    if (pendingTransactionId) {
      return { pendingTransactionId };
    }
    return yield signTxRes;
  });
}
function sendTransaction(ctx, share, walletId, userId, tx, chainId) {
  return __async(this, null, function* () {
    const protocolId = uuid.v4();
    const sendTransactionRes = ctx.client.sendTransaction(userId, walletId, { transaction: tx, chainId, protocolId });
    if (ctx.offloadMPCComputationURL && !ctx.useDKLS) {
      return sendTransactionRequest(ctx, userId, walletId, protocolId, tx, share, chainId);
    }
    const serverUrl = getBaseMPCNetworkUrl(ctx.env, !ctx.disableWebSockets);
    const sendTransactionFn = ctx.useDKLS ? globalThis.dklsSendTransaction : globalThis.sendTransaction;
    const parsedShare = JSON.parse(share);
    if (!parsedShare.disableWebSockets !== !ctx.disableWebSockets) {
      parsedShare.disableWebSockets = ctx.disableWebSockets;
    }
    share = JSON.stringify(parsedShare);
    const sendTxRes = function() {
      return __async(this, null, function* () {
        try {
          return yield new Promise(
            (resolve, reject) => sendTransactionFn(share, serverUrl, tx, chainId, protocolId, (err, result) => {
              if (err) {
                reject(err);
              }
              resolve({ signature: result });
            })
          );
        } catch (e) {
          throw new Error(`error signing transaction to send for account with userId ${userId} and walletId ${walletId}`);
        }
      });
    }();
    const {
      data: { pendingTransactionId }
    } = yield sendTransactionRes;
    if (pendingTransactionId) {
      return { pendingTransactionId };
    }
    return yield sendTxRes;
  });
}
function refresh(ctx, share, walletId, userId, oldPartnerId, newPartnerId, keyShareProtocolId) {
  return __async(this, null, function* () {
    const {
      data: { protocolId }
    } = yield ctx.client.refreshKeys(userId, walletId, oldPartnerId, newPartnerId, keyShareProtocolId);
    const serverUrl = getBaseMPCNetworkUrl(ctx.env, !ctx.disableWebSockets);
    const refreshFn = ctx.useDKLS ? globalThis.dklsRefresh : globalThis.refresh;
    const parsedShare = JSON.parse(share);
    if (!parsedShare.disableWebSockets !== !ctx.disableWebSockets) {
      parsedShare.disableWebSockets = ctx.disableWebSockets;
    }
    share = JSON.stringify(parsedShare);
    try {
      return yield new Promise(
        (resolve, reject) => refreshFn(share, serverUrl, protocolId, (err, result) => {
          if (err) {
            reject(err);
          }
          resolve({ protocolId, signer: result });
        })
      );
    } catch (e) {
      throw new Error(`error refreshing keys for account with userId ${userId} and walletId ${walletId}`);
    }
  });
}
function getPrivateKey(ctx, share, walletId, userId) {
  return __async(this, null, function* () {
    const paraShare = yield ctx.client.getParaShare(userId, walletId);
    if (!paraShare) {
      return "";
    }
    try {
      return yield new Promise(
        (resolve, reject) => globalThis.getPrivateKey(share, paraShare, (err, result) => {
          if (err) {
            reject(err);
          }
          resolve(result);
        })
      );
    } catch (e) {
      throw new Error(`error getting private key for account with userId ${userId} and walletId ${walletId}`);
    }
  });
}
export {
  ed25519Keygen,
  ed25519PreKeygen,
  ed25519Sign,
  getPrivateKey,
  keygen,
  preKeygen,
  refresh,
  sendTransaction,
  signMessage,
  signTransaction
};
